import { useQuery } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'

type GetDormakabaAccessPointsRequest = {
  buildingId: number
}

export type DormakabaCommonArea = {
  accessMode: string | null
  commonAreaId: number
  isExternal: boolean
  name: string
  type: 'internal' | 'external'
}

type GetDormakabaAccessPointsResponse = {
  commonAreas?: DormakabaCommonArea[]
}

// This endpoint can hang indefinitely for some buildings and axios has no default timeout, so
// cap it — on timeout the query errors and the tab shows its "not connected" state.
export const DORMAKABA_ACCESS_POINTS_TIMEOUT_MS = 8_000

export const getDormakabaAccessPointsQueryKey = (data: GetDormakabaAccessPointsRequest) => [
  'useGetDormakabaAccessPoints',
  data,
]

// Unlike `get-dormakaba-connection.ts` (a DB-only config read), this is a live call to
// Dormakaba's Community Server — it fails when the stored credentials are broken.
const getDormakabaAccessPoints = async (data: GetDormakabaAccessPointsRequest) => {
  const response = await authAPI.get<GetDormakabaAccessPointsResponse | null>(
    `/dormakaba/access-points/${data.buildingId}`,
    { timeout: DORMAKABA_ACCESS_POINTS_TIMEOUT_MS },
  )
  return response.data
}

const selectCommonAreas = (data: GetDormakabaAccessPointsResponse | null) => data?.commonAreas ?? []

export const useGetDormakabaAccessPoints = (
  data: GetDormakabaAccessPointsRequest,
  enabled = true,
) =>
  useQuery({
    enabled: enabled && Boolean(data.buildingId),
    queryFn: () => getDormakabaAccessPoints(data),
    queryKey: getDormakabaAccessPointsQueryKey(data),
    // A hanging/timed-out probe is signal enough on its own; retrying just delays the error state.
    refetchOnWindowFocus: false,
    retry: false,
  })

export const useGetDormakabaCommonAreas = (
  data: GetDormakabaAccessPointsRequest,
  { enabled = true }: { enabled?: boolean } = {},
) =>
  useQuery({
    enabled: enabled && Boolean(data.buildingId),
    queryFn: () => getDormakabaAccessPoints(data),
    queryKey: getDormakabaAccessPointsQueryKey(data),
    refetchOnWindowFocus: false,
    retry: false,
    select: selectCommonAreas,
    staleTime: 5 * 60 * 1000,
  })
